/**
 * Erweiterte Tag-Taxonomie für Business-fokussiertes Content-Marketing
 * Hohe Tiefe für maximale SEO und Content-Vernetzung
 */

export interface BlogTag {
  id: string;
  name: string;
  level: number;
  parent?: string;
  description?: string;
  seoKeyword?: string;
  color?: string;
}

// Level 1: Hauptkategorien
export const mainCategories: BlogTag[] = [
  {
    id: 'outdoor-coaching',
    name: 'Outdoor-Coaching',
    level: 1,
    description: 'Coaching-Sessions in der Natur für Führungskräfte',
    seoKeyword: 'Outdoor Coaching Bayern',
    color: 'emerald'
  },
  {
    id: 'executive-coaching',
    name: 'Executive-Coaching',
    level: 1,
    description: 'Führungskräfte-Entwicklung und Leadership-Coaching',
    seoKeyword: 'Executive Coach Bayern',
    color: 'blue'
  },
  {
    id: 'finanzcoaching',
    name: 'Finanzcoaching',
    level: 1,
    description: 'Finanzielle Bildung und Investment-Psychologie',
    seoKeyword: 'Finanzcoach München',
    color: 'amber'
  }
];

// Level 2: Methoden
export const methodTags: BlogTag[] = [
  {
    id: 'walk-talk-coaching',
    name: 'Walk-Talk-Coaching',
    level: 2,
    parent: 'outdoor-coaching',
    description: 'Coaching im Gehen für bessere Reflexion',
    seoKeyword: 'Walk and Talk Coaching'
  },
  {
    id: 'natur-coaching',
    name: 'Natur-Coaching',
    level: 2,
    parent: 'outdoor-coaching',
    description: 'Coaching-Methoden mit Naturverbindung',
    seoKeyword: 'Business Coaching Natur'
  },
  {
    id: 'business-coaching-outdoor',
    name: 'Business-Coaching-Outdoor',
    level: 2,
    parent: 'executive-coaching',
    description: 'Geschäftliche Herausforderungen draußen lösen',
    seoKeyword: 'Business Coach outdoor'
  }
];

// Level 3: Locations (business-friendly, kein "Retreat")
export const locationTags: BlogTag[] = [
  {
    id: 'bayern-coaching',
    name: 'Bayern-Coaching',
    level: 3,
    parent: 'outdoor-coaching',
    description: 'Coaching-Angebote in ganz Bayern',
    seoKeyword: 'Coach Bayern'
  },
  {
    id: 'rothsee-coaching',
    name: 'Rothsee-Coaching',
    level: 3,
    parent: 'bayern-coaching',
    description: 'Coaching-Sessions am Rothsee bei Roth',
    seoKeyword: 'Coaching Rothsee'
  },
  {
    id: 'alpen-coaching',
    name: 'Alpen-Coaching',
    level: 3,
    parent: 'bayern-coaching',
    description: 'Business-Coaching in den Bayerischen Alpen',
    seoKeyword: 'Alpen Business Coaching'
  },
  {
    id: 'eibsee-sessions',
    name: 'Eibsee-Sessions',
    level: 3,
    parent: 'alpen-coaching',
    description: 'Leadership-Coaching am Eibsee',
    seoKeyword: 'Eibsee Leadership Coaching'
  }
];

// Level 4: Zielgruppen & Themen
export const topicTags: BlogTag[] = [
  {
    id: 'fuehrungskraefte-coaching',
    name: 'Führungskräfte-Coaching',
    level: 4,
    parent: 'executive-coaching',
    description: 'Coaching für Führungskräfte aller Ebenen',
    seoKeyword: 'Führungskräfte Coach'
  },
  {
    id: 'cfo-coaching',
    name: 'CFO-Coaching',
    level: 4,
    parent: 'fuehrungskraefte-coaching',
    description: 'Spezialisiertes Coaching für Finanzvorstände',
    seoKeyword: 'CFO Coach'
  },
  {
    id: 'burnout-praevention',
    name: 'Burnout-Prävention',
    level: 4,
    parent: 'executive-coaching',
    description: 'Präventive Maßnahmen gegen Burnout',
    seoKeyword: 'Burnout Prävention Coaching'
  },
  {
    id: 'leadership-transformation',
    name: 'Leadership-Transformation',
    level: 4,
    parent: 'fuehrungskraefte-coaching',
    description: 'Führungsstil weiterentwickeln und transformieren',
    seoKeyword: 'Leadership Coach'
  },
  {
    id: 'entscheidungsfindung',
    name: 'Entscheidungsfindung',
    level: 4,
    parent: 'executive-coaching',
    description: 'Bessere Entscheidungen in komplexen Situationen',
    seoKeyword: 'Entscheidungscoaching'
  },
  {
    id: 'teamkonflikt-loesung',
    name: 'Teamkonflikt-Lösung',
    level: 4,
    parent: 'leadership-transformation',
    description: 'Konfliktlösung und Teamentwicklung',
    seoKeyword: 'Teamkonflikt Coaching'
  }
];

// Level 5: Spezifische Kombinationen (nur wenn sinnvoll)
export const specificTags: BlogTag[] = [
  {
    id: 'eibsee-leadership-sessions',
    name: 'Eibsee-Leadership-Sessions',
    level: 5,
    parent: 'eibsee-sessions',
    description: 'Intensive Leadership-Entwicklung am Eibsee',
    seoKeyword: 'Eibsee Leadership Coaching'
  },
  {
    id: 'rothsee-cfo-coaching',
    name: 'Rothsee-CFO-Coaching',
    level: 5,
    parent: 'rothsee-coaching',
    description: 'CFO-spezifische Coaching-Sessions am Rothsee',
    seoKeyword: 'CFO Coaching Rothsee'
  }
];

// Kombiniere alle Tags
export const allBlogTags: BlogTag[] = [
  ...mainCategories,
  ...methodTags,
  ...locationTags,
  ...topicTags,
  ...specificTags
];

/**
 * Get tags by level
 */
export const getTagsByLevel = (level: number): BlogTag[] => {
  return allBlogTags.filter(tag => tag.level === level);
};

/**
 * Get child tags for a parent
 */
export const getChildTags = (parentId: string): BlogTag[] => {
  return allBlogTags.filter(tag => tag.parent === parentId);
};

/**
 * Get tag by ID
 */
export const getTagById = (id: string): BlogTag | undefined => {
  return allBlogTags.find(tag => tag.id === id);
};

/**
 * Get tags by name (for existing blog posts)
 */
export const getTagsByNames = (names: string[]): BlogTag[] => {
  return names.map(name => {
    const normalizedName = name.toLowerCase().replace(/\s+/g, '-');
    return allBlogTags.find(tag => 
      tag.id === normalizedName || 
      tag.name.toLowerCase() === name.toLowerCase()
    );
  }).filter(Boolean) as BlogTag[];
};

/**
 * Generate SEO-optimized tag combinations for content
 */
export const generateTagCombinations = (primaryTag: string): string[] => {
  const combinations: string[] = [primaryTag];
  const tag = getTagById(primaryTag);
  
  if (!tag) return combinations;

  // Add parent tags
  let current = tag;
  while (current.parent) {
    combinations.unshift(current.parent);
    current = getTagById(current.parent)!;
  }

  // Add relevant sibling tags
  if (tag.parent) {
    const siblings = getChildTags(tag.parent);
    siblings.forEach(sibling => {
      if (sibling.id !== tag.id && !combinations.includes(sibling.id)) {
        combinations.push(sibling.id);
      }
    });
  }

  return combinations.slice(0, 8); // Max 8 tags per post
};